// bibliotecas externas
import React, { useState, useEffect, useContext, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Table,
  Col,
  Row,
  Card,
  CardBody,
  CardTitle,
  Button,
  Badge,
  Spinner,
} from "reactstrap";
import SweetAlert from "react-bootstrap-sweetalert";
import { isMobile } from "react-device-detect";
import { useNavigate } from "react-router-dom";

// componentes
import PaginationV2 from "../../components/PaginationV2";
import HostingRestrictionIcon from "../../components/HostingRestrictionIcon";
import Stepper from "../../components/Stepper";

// services
import {
  getParticipants,
  getRoommatesRoom,
} from "../../utils/services/roommates";

// context
import { ParticipantContext } from "../../providers/participant";
import { MatchContext } from "../../providers/match";

// hooks
import useDebounce from "../../hooks/useDebounce";

// store
import { createUser, reset } from "../../store/users";

// utils
import { Ienums } from "../../utils/interface";
import { getBackgroundGradient } from "../../utils/color";

export default function Participants() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [participants, setParticipants] = useState([]);
  const [user, setUser] = useState();
  //paginação
  const [currentIndex, setCurrentIndex] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [countData, setCountData] = useState(1);
  const [room, setRoom] = useState("");
  // tamanho do quarto do parametro
  const [roomParams, setRoomParams] = useState(null);
  //confirmação
  const [confirmAlert, setConfirmAlert] = useState(false);
  const [successDlg, setSuccessDlg] = useState(false);
  const [successDlgMe, setSuccessDlgMe] = useState(false);
  // carregamento
  const [loading, setLoading] = useState(true);
  // busca/paginação em andamento (após o carregamento inicial)
  const [fetching, setFetching] = useState(false);

  const { t, idParticipant, idEvent, i18n } = useContext(ParticipantContext);

  const { matchConfig } = useContext(MatchContext);

  const debouncedSearch = useDebounce(search, 700);

  // identifica a requisição mais recente de getAllParticipant, para ignorar respostas de requisições antigas que cheguem fora de ordem
  const requestIdRef = useRef(0);

  const dispatch = useDispatch();
  const { users, status } = useSelector((state) => state.user);

  // verifica se o convidado já foi escolhido; caso contrário busca a sala do participante e abre a confirmação de seleção
  async function selectRoommate(el) {
    if (users.some((e) => e.email === el.email)) {
      setSuccessDlg(true);
      return;
    }
    await getRoommatesRoom(idEvent, idParticipant).then((res) => {
      if (res.message === Ienums.CHOOSEN) {
        setRoom(res.data.roomSize);
        setSuccessDlgMe(true);
        return;
      } else {
        setUser(el);
        setConfirmAlert(true);
      }
    });
  }

  // cria o usuário escolhido no redux e navega para a tela de match
  async function confirmSelection() {
    dispatch(
      createUser({
        id: user.id,
        name: user.name,
        email: user.email,
        type: Ienums.CHOOSEN,
        status: Ienums.PENDING_SEND,
        order: users.length + 1,
        picture: user.picture ?? null,
      }),
    );
    setConfirmAlert(false);
    navigate({ pathname: "/match", search: window.location.search });
  }

  // busca a lista paginada/filtrada de participantes; ignora respostas de requisições antigas (corrida entre paginação e busca)
  async function getAllParticipant() {
    const requestId = ++requestIdRef.current;
    setFetching(true);

    if (debouncedSearch.length > 2 || debouncedSearch.length === 0)
      await getParticipants({
        eventId: idEvent,
        loginId: idParticipant,
        offset: currentIndex,
        limit: itemsPerPage,
        search: debouncedSearch,
      })
        .then((res) => {
          if (requestId !== requestIdRef.current) return;
          setParticipants(res.data);
          setCountData(res.count);
        })
        .catch((error) => console.log("function error", error));

    if (requestId === requestIdRef.current) {
      setLoading(false);
      setFetching(false);
    }
  }

  useEffect(() => {
    if (idEvent && idParticipant) {
      getAllParticipant();
    }
  }, [idEvent, idParticipant, currentIndex, itemsPerPage]);

  useEffect(() => {
    if (idEvent && idParticipant) {
      getAllParticipant();
    }
  }, [debouncedSearch]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (params) setRoomParams(params.get("room") || null);
  }, []);

  const hasHostingRestriction = participants.some(
    (p) => !!p?.hostingRestriction,
  );

  return (
    <div
      className="container-main"
      style={{
        background: getBackgroundGradient(matchConfig?.colorBackground),
        padding: "20px 0",
      }}
    >
      <Row
        style={{
          display: "flex",
          justifyContent: "center",
          margin: "20px 0",
        }}
      >
        {confirmAlert ? (
          <SweetAlert
            title={`${t("Deseja selecionar")} ${user.name} ${t(
              "como parceiro(a) de quarto?",
            )}`}
            warning
            showCancel
            // botões de confirmar e cancelar trocados para alterar a ordem de exibição!!!!!!!!
            confirmBtnText={t("Não")}
            cancelBtnText={t("Sim")}
            confirmBtnBsStyle="danger"
            cancelBtnBsStyle="success"
            onConfirm={() => {
              setConfirmAlert(false);
            }}
            onCancel={() => confirmSelection()}
          >
            {roomParams && roomParams === "2"
              ? t(
                  "Após a dupla aceitar o convite, a escolha do quarto será confirmada e não poderá ser alterada",
                )
              : t(
                  "Após o aceite dos convites, a escolha do quarto será confirmada e não poderá ser alterada",
                )}
          </SweetAlert>
        ) : null}

        {successDlg ? (
          <SweetAlert
            danger
            title={t("Esse participante já foi escolhido")}
            onConfirm={() => {
              setSuccessDlg(false);
            }}
          ></SweetAlert>
        ) : null}
        {successDlgMe ? (
          <SweetAlert
            danger
            title={t("Você recebeu uma solicitação de hospedagem!")}
            onConfirm={() => {
              setSuccessDlgMe(false);
              dispatch(reset([]));
              navigate({
                pathname: "/match",
                search: `?idEvent=${idEvent}&idPart=${idParticipant}&room=${room}&idiom=${i18n.language}`,
              });
            }}
          ></SweetAlert>
        ) : null}
        <Col md={10} style={{ margin: 0, padding: 0 }}>
          <Stepper
            currentStep={3}
            t={t}
            activeColor={matchConfig?.colorBackground}
          />
          <Card className="room-card-main">
            {loading ? (
              <CardBody
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <span style={{ marginRight: 10, fontSize: 18 }}>
                  {t("Carregando")}
                </span>
                <Spinner />
              </CardBody>
            ) : (
              <CardBody>
                <CardTitle className="room-card-title participant-title">
                  {t("ESCOLHA SEU PARCEIRO(A) DE QUARTO")}
                </CardTitle>
                <p>
                  {t(
                    "Não encontrou quem procura? Essa pessoa pode já ter sido vinculada a outra dupla. Confira a lista e escolha outra pessoa.",
                  )}
                </p>
                <Col md={5}>
                  <div className="app-search d-lg-block events-input-search participant-search">
                    <span>
                      <i className="bx bx-search"></i>
                    </span>
                    <input
                      className="form-control"
                      type="text"
                      placeholder={t("Pesquise o(a) convidado(a)")}
                      aria-label={t("Pesquise o(a) convidado(a)")}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    {fetching && (
                      <Spinner
                        size="sm"
                        className="participant-search-spinner"
                      />
                    )}
                  </div>
                </Col>
                <div className="table-responsive">
                  {!isMobile && !window.ReactNativeWebView && participants ? (
                    <Table className="table mb-0 table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>{t("Nome e Email")}</th>
                          {hasHostingRestriction && <th>{t("Restrição")}</th>}
                          {matchConfig?.showPositionInList && (
                            <th>{t("Cargo")}</th>
                          )}
                          <th>{t("Opção")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {participants?.length > 0 ? (
                          participants.map((e) => (
                            <tr key={e.id}>
                              <td>
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                  }}
                                >
                                  <span>{e.name} -</span>
                                  <span>{e.email}</span>
                                </div>
                              </td>
                              {hasHostingRestriction && (
                                <td>
                                  {e?.hostingRestriction ? (
                                    <HostingRestrictionIcon
                                      hostingRestriction={e.hostingRestriction}
                                      idiom={t}
                                    />
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                              )}
                              {matchConfig?.showPositionInList && (
                                <td>
                                  {e.cargo ? (
                                    <Badge color="light" className="text-dark">
                                      {e.cargo}
                                    </Badge>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                              )}
                              <td>
                                <Button
                                  color="success"
                                  className="btn-select btn btn-success waves-effect waves-light"
                                  style={{
                                    backgroundColor: matchConfig?.colorButton,
                                  }}
                                  onClick={() => {
                                    selectRoommate(e);
                                  }}
                                >
                                  {t("Convidar")}
                                </Button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={
                                3 +
                                (hasHostingRestriction ? 1 : 0) +
                                (matchConfig?.showPositionInList ? 1 : 0)
                              }
                              className="text-center text-muted"
                            >
                              {t("Nenhum convidado encontrado")}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  ) : (
                    <Table className="table mb-0 table-sm table-striped">
                      <thead className="table-light">
                        <tr>
                          <th>{t("Opção")}</th>
                          <th>{t("Nome e Email")}</th>
                          {hasHostingRestriction && <th>{t("Restrição")}</th>}
                          {matchConfig?.showPositionInList && (
                            <th>{t("Cargo")}</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const filtered = participants
                            .filter(
                              (e) =>
                                e.name
                                  .toLocaleLowerCase()
                                  .indexOf(search.toLocaleLowerCase()) > -1,
                            )
                            .filter((e) => e.id !== idParticipant);

                          if (filtered.length === 0) {
                            return (
                              <tr>
                                <td
                                  colSpan={
                                    3 +
                                    (hasHostingRestriction ? 1 : 0) +
                                    (matchConfig?.showPositionInList ? 1 : 0)
                                  }
                                  className="text-center text-muted"
                                  style={{ fontSize: "12px" }}
                                >
                                  {t("Nenhum convidado encontrado")}
                                </td>
                              </tr>
                            );
                          }

                          return filtered.map((e) => (
                            <tr key={e.id}>
                              <td>
                                <Button
                                  color="success"
                                  className="btn btn-success waves-effect waves-light rounded-circle d-flex align-items-center justify-content-center p-0"
                                  onClick={() => {
                                    selectRoommate(e);
                                  }}
                                  title={t("Convidar")}
                                  aria-label={t("Convidar")}
                                  style={{
                                    width: 32,
                                    height: 32,
                                    backgroundColor:
                                      matchConfig?.colorButton ?? "#0E2738",
                                    borderColor:
                                      matchConfig?.colorButton ?? "#0E2738",
                                  }}
                                >
                                  <i
                                    className="bx bx-user-plus text-white"
                                    style={{ fontSize: 16 }}
                                  />
                                </Button>
                              </td>
                              <td
                                style={{
                                  fontSize: "12px",
                                  maxWidth: "20vw",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                  }}
                                >
                                  <span>{e.name} -</span>
                                  <span>{e.email}</span>
                                </div>
                              </td>
                              {hasHostingRestriction && (
                                <td>
                                  {e?.hostingRestriction ? (
                                    <HostingRestrictionIcon
                                      hostingRestriction={e.hostingRestriction}
                                      idiom={t}
                                    />
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                              )}
                              {matchConfig?.showPositionInList && (
                                <td>
                                  {e.cargo ? (
                                    <Badge
                                      color="light"
                                      className="text-dark"
                                      style={{ fontSize: "11px" }}
                                    >
                                      {e.cargo}
                                    </Badge>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                              )}
                            </tr>
                          ));
                        })()}
                      </tbody>
                    </Table>
                  )}
                </div>
              </CardBody>
            )}
          </Card>

          {!loading && (
            <PaginationV2
              data={participants}
              search={search}
              currentIndex={currentIndex}
              setCurrentIndex={setCurrentIndex}
              countData={countData}
              maxItemPerPage={itemsPerPage}
              setMaxItemPerPage={setItemsPerPage}
              maxPageButtonsToShow={3}
            />
          )}
        </Col>
      </Row>
    </div>
  );
}
